/**
 * `/give <target> item <name>` (spec 2026-09-22): every Storage item that is
 * not a bag or a potion — those two have their own gift kinds — handed over one
 * at a time. The names are the command spellings.
 *
 * A gift always puts a FRESH one on the shelf: a Diamond, Moon Stone or Variety
 * Orb the player already used or opened comes back as `stored`. That is the
 * difference from the older `/give <t> moonstone 1`, which refuses to un-use a
 * spent stone, and it is deliberate — the command exists for devs handing an
 * item over, and "you already had one once" is no reason for it to do nothing.
 *
 * `supabase/migrations/*_golden_takeout.sql` (dev_give) holds a copy of this list;
 * tests/unit/giftItems.test.ts keeps the two identical.
 */
export const GIFT_ITEMS = [
  'diamond',
  'varietyorb',
  'moonstone',
  'debris',
  'goldstone',
  'capsule',
  // The Golden Takeout Bag (spec 2026-09-26) — `/give <t> goldenbags N`
  // gives several at once; this is the one-at-a-time spelling.
  'goldenbag',
  // The Event Archive's prize (spec 2026-10-04).
  'capystone',
  // The Dark Shadows' two prizes (spec 2026-10-07): a shard is a count, the
  // Evil Orb a one-of.
  'darkshard',
  'evilorb',
  // Above the Clouds' prize (spec 2026-10-08): a count, like the Dark Shard.
  'lightshard',
  // Heaven's Peak's Light Orb, a one-of, and the Origin chests, counts
  // (spec 2026-10-13).
  'lightorb',
  'darkchest',
  'lightchest',
  // The Fungi Forest's two items (spec 2026-10-24), counts.
  'shroom',
  'snowshroom',
  // The Shroombellion's prize (spec 2026-10-27), a one-of like the Evil Orb.
  'tokenofshroom',
] as const;

export type GiftItem = (typeof GIFT_ITEMS)[number];

export function isGiftItem(v: unknown): v is GiftItem {
  return typeof v === 'string' && (GIFT_ITEMS as readonly string[]).includes(v);
}

/** The Storage art for each item — what the delivery popup shows. */
export const GIFT_ITEM_ART: Record<GiftItem, string> = {
  diamond: 'diamond',
  varietyorb: 'orb-variety',
  moonstone: 'item-moonstone',
  debris: 'item-debris',
  goldstone: 'item-goldstone',
  capsule: 'capsule-capy',
  goldenbag: 'takeout-golden',
  capystone: 'item-capystone',
  darkshard: 'item-darkshard',
  evilorb: 'item-evilorb',
  lightshard: 'item-lightshard',
  // The Light Orb IS the orb on Heaven's Peak — the same sprite.
  lightorb: 'decor-heavenorb',
  darkchest: 'item-darkchest',
  lightchest: 'item-lightchest',
  shroom: 'item-shroom',
  snowshroom: 'item-snowshroom',
  tokenofshroom: 'item-tokenofshroom',
};
